import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { TableRow } from "../utils/tables-metadata";
import { AllergyTable } from "./allergy";
import { CareGapsTable } from "./care-gaps";
import { CarePlanTable } from "./care-plan";
import { CareTeamTable } from "./care-team";
import { ConditionTable } from "./condition";
import { CoverageTable } from "./coverage";
import { EncounterTable } from "./encounter";
import { FamilyHistoryTable } from "./family-history";
import { ImmunizationTable } from "./immunization";
import { LabTable } from "./lab";
import { MedicationTable } from "./medication";
import { ImagingReportsTable, NotesTable } from "./notes";
import { ProcedureTable } from "./procedure";
import { RawDocumentsTable } from "./raw-documents";
import { RelatedPersonTable } from "./related-person";
import { SocialHistoryTable } from "./social-history";
import { SurveyTable } from "./survey";
import { SuspectTable } from "./suspect";
import { VitalTable } from "./vital";

interface TableContentProps {
  tableType: string;
  displayName: string;
  filteredRows: TableRow[];
  fhirSdk: FhirBundleSdk;
  filterQuery?: string;
  handleRowClick: (row: TableRow, tableType: string, suspectsContext?: string) => void;
  handlePdfView: (docId: string) => void;
  isRowViewed: (tableType: string, rowId: string) => boolean;
  onDocumentDownloaded?: (docId: string) => void;
}

/**
 * Renders the appropriate table component based on tableType
 */
export function TableContent({
  tableType,
  displayName,
  filteredRows,
  fhirSdk,
  filterQuery = "",
  handleRowClick,
  handlePdfView,
  isRowViewed,
  onDocumentDownloaded,
}: TableContentProps) {
  // NOTE: Do not try and extract this duplicative code into a lookup dictionary!
  // Straightforward dumb code here will be easier to tweak and will lead to less tangled wires.

  // Helper function to check if a row is viewed for this specific table type
  const isRowViewedHelper = (row: TableRow) => isRowViewed(tableType, row.id);

  const commonProps = {
    fhirSdk,
    filterQuery,
    onPdfView: handlePdfView,
    onRowClick: (row: TableRow) => handleRowClick(row, tableType),
    isRowViewed: isRowViewedHelper,
  };

  switch (tableType) {
    case "CareGaps":
      return <CareGapsTable rows={filteredRows} {...commonProps} />;

    case "Suspects":
      return <SuspectTable rows={filteredRows} {...commonProps} />;

    case "DiagnosticReport":
      return (
        <NotesTable
          rows={filteredRows}
          {...commonProps}
          resourceType="DiagnosticReport"
          displayName={displayName}
        />
      );

    case "ImagingReports":
      return <ImagingReportsTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "AllergyIntolerance":
      return <AllergyTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "Procedure":
      return <ProcedureTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "Immunization":
      return <ImmunizationTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "FamilyMemberHistory":
      return <FamilyHistoryTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "RelatedPerson":
      return <RelatedPersonTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "Coverage":
      return <CoverageTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "Encounter":
      return <EncounterTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "CareTeam":
      return <CareTeamTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "Condition":
      return <ConditionTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "Medication":
      return <MedicationTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "LaboratoryObservations":
      return <LabTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "VitalSignObservations":
      return <VitalTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "SurveysObservations":
      return <SurveyTable rows={filteredRows} {...commonProps} />;

    case "SocialHistoryObservations":
      return <SocialHistoryTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "CarePlan":
      return <CarePlanTable rows={filteredRows} displayName={displayName} {...commonProps} />;

    case "DocumentReference":
      return (
        <RawDocumentsTable
          rows={filteredRows}
          fhirSdk={fhirSdk}
          filterQuery={filterQuery}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onRowClick={() => {}}
          onDocumentDownloaded={onDocumentDownloaded}
          isRowViewed={isRowViewedHelper}
        />
      );

    default:
      console.warn(`Unknown tableType: ${tableType} - not migrated to new architecture`);
      return null;
  }
}
